/* eslint-disable @next/next/no-img-element */
import LinkedInIcon from '@mui/icons-material/LinkedIn'
import XIcon from '@mui/icons-material/X'
import { Typography, Box, CircularProgress } from '@mui/material'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import { useEffect, useState, useCallback, useMemo, FC } from 'react'
import Button from '@/src/components/button'
import { useStore } from '@/src/types/store'
import CommonApi from '@/pages/api'
import { RandomImage } from '../utils'
import {
  escapeHtml,
  formatDate,
  getSocialPostMediaUrl,
  unescapeSocialText
} from '@/src/types/helper'
import { LinkedInPostIcon, TwitterPostIcon } from '@/src/assets/icons'
import SocialPostMedia from '@/src/components/SocialPostMedia'

// Above this many posts the section scrolls horizontally instead of wrapping in a grid.
const GRID_POST_LIMIT = 4

interface MediaSectionProps {
  sectionId: number
  isDraft?: boolean
}

const MediaSection = ({ sectionId, isDraft = false }: MediaSectionProps) => {
  const [loading, setLoading] = useState(false)
  const [mediaSectionData, setMediaSectionData] = useState<any>()
  const [SocialProfile, setSocialProfile] = useState<any>()
  const { themeLogo, TextColorType, profile, homeTheme } = useStore()

  const PLATFORM_ICON_MAP: Record<string, FC<any>> = useMemo(
    () => ({
      linkedin: LinkedInPostIcon,
      twitter: TwitterPostIcon
    }),
    []
  )

  const fetchMediaSectionData = useCallback(async () => {
    setLoading(true)
    try {
      const response = await CommonApi({
        method: 'get',
        path: `show-social-posts/${sectionId}${isDraft ? '?status=draft' : ''}`
      })
      if (response?.status) {
        setMediaSectionData(response?.data)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }, [isDraft, sectionId])

  const fetchSocialProfile = useCallback(async () => {
    try {
      const response = await CommonApi({
        method: 'get',
        path: `show-social-profiles`
      })
      if (response.status) {
        setSocialProfile(response?.data)
      } else {
      }
    } catch (error) {
      console.log(error)
    }
  }, [])

  useEffect(() => {
    fetchMediaSectionData()
    fetchSocialProfile()
  }, [fetchMediaSectionData, fetchSocialProfile])

  // Up to four posts still fit the grid. Beyond that they become a horizontal strip, the
  // same pattern the admin recent-posts and announcements rows use.
  const isCarousel = (mediaSectionData?.length ?? 0) > GRID_POST_LIMIT

  return (
    <Box className={`w-full flex justify-center`}>
      <Box className="w-full grid grid-cols-1 justify-center gap-6">
        {loading ? (
          <Box className="w-full flex justify-center items-center h-[300px]">
            <CircularProgress size={25} />
          </Box>
        ) : (
          <>
            <Box className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0">
              <Typography variant="h5" className="block w-full sm:w-auto">
                Social media posts
              </Typography>

              <Box className="flex gap-4">
                {SocialProfile?.twitter_url && (
                  <Button
                    onClick={() => window.open(SocialProfile?.twitter_url, '_blank')}
                    variant="text"
                    text="Follow on X"
                    extraClass={`${
                      TextColorType?.primaryTextDark
                        ? 'text-primary-home'
                        : TextColorType?.secondaryTextDark
                          ? 'text-secondary-home'
                          : 'text-black'
                    }  rounded-[100px]`}
                    customStyle={true}
                    icon={<XIcon />}
                  />
                )}

                {SocialProfile?.linkedin_url && (
                  <Button
                    onClick={() => window.open(SocialProfile?.linkedin_url, '_blank')}
                    variant="text"
                    text="Follow on LinkedIn"
                    extraClass={`${
                      TextColorType?.primaryTextDark
                        ? 'text-primary-home'
                        : TextColorType?.secondaryTextDark
                          ? 'text-secondary-home'
                          : 'text-black'
                    }  rounded-[100px]`}
                    customStyle={true}
                    icon={<LinkedInIcon sx={{ color: 'primary-home' }} />}
                  />
                )}
              </Box>
            </Box>
            {mediaSectionData?.length === 0 ? (
              <Box className="flex justify-center items-center h-[50px] font-semibold text-balance ">
                {' '}
                <em>There are no recent posts.</em>
              </Box>
            ) : (
              <Box
                className={
                  isCarousel
                    ? 'w-full min-w-0 flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3'
                    : 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4'
                }
              >
                {mediaSectionData?.map((val: any, index: number) => {
                  const media = val?.platforms?.[0]?.media_urls?.[0]
                  const pdfUrl =
                    media?.type === 'pdf'
                      ? getSocialPostMediaUrl(val?.related_content, media?.url, {
                          is_custom_site: homeTheme?.is_custom_site,
                          custom_site_announcements_url: homeTheme?.custom_site_announcements_url
                        })
                      : ''

                  return (
                    <Box
                      key={val?.id ?? index}
                      className={`bg-white rounded-[20px] p-4 gap-6 shadow-md ${
                        isCarousel ? 'shrink-0 snap-start w-[85%] sm:w-[20rem]' : ''
                      }`}
                    >
                      <Box className="flex justify-between gap-2">
                        <Box className="flex gap-2">
                          <img
                            src={themeLogo.logo_mark}
                            alt="logo mark"
                            className="!h-[35px] !w-[35px] shrink-0 object-contain self-end mb-1.5"
                          />
                          <Box>
                            <Typography variant="caption">
                              {formatDate(val?.posted_date, 'long')}
                            </Typography>
                            <Typography variant="subtitle2">{profile.business_name}</Typography>
                          </Box>
                        </Box>
                        <Box className="flex gap-2 items-center">
                          {val?.platforms?.map((platform: any) => {
                            const Icon = PLATFORM_ICON_MAP[platform.platform]
                            if (!Icon || !platform.post_url) return null
                            return (
                              <button
                                key={platform.id}
                                type="button"
                                onClick={() =>
                                  window.open(platform.post_url, '_blank', 'noopener,noreferrer')
                                }
                                className="hover:opacity-80 transition"
                                aria-label="View Social Post"
                              >
                                <Icon className="h-6 w-6" />
                              </button>
                            )
                          })}
                        </Box>
                      </Box>
                      <Box className="mt-4">
                        <Typography
                          variant="body2"
                          className="line-clamp-6 min-h-[7.5rem]"
                          dangerouslySetInnerHTML={{
                            __html: escapeHtml(
                              unescapeSocialText(val?.platforms?.[0]?.post_content)
                            ).replaceAll('\n', '<br>')
                          }}
                        />
                      </Box>
                      <Box className="overflow-hidden">
                        {media ? (
                          pdfUrl ? (
                            <a
                              href={pdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Open the attached PDF"
                              className="block cursor-pointer"
                            >
                              <SocialPostMedia type={media?.type} url={media?.url} alt="media" />
                            </a>
                          ) : (
                            <SocialPostMedia type={media?.type} url={media?.url} alt="media" />
                          )
                        ) : (
                          <RandomImage />
                        )}
                      </Box>
                      <Box className="flex justify-between items-center mt-3">
                        <Box className="flex items-center gap-[6px]">
                          <FavoriteBorderIcon />
                          {val.total.reactions}
                        </Box>
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  )
}

export default MediaSection
